import { useEffect } from "react";
import { Lock, Pencil, Check, Quote } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { useAnnotatedTextSession } from "@/lib/annotatedTextQueries";
import { useAnnotationLease } from "@/hooks/useAnnotationLease";
import AutosaveStatus from "../../presentation/AutosaveStatus";
import { useRoomBlockRegistry } from "../RoomBlockRegistry";

/**
 * Master v5 Prompt 25 — Annotated Text for Voice, the first advanced
 * layer renderer vertical slice. Mounted by RoomShell exactly like every
 * other renderer (same header/Save&Exit/Submit chrome).
 *
 * Block decomposition is dynamic — one block per StructureBeat in the
 * AnnotatedTextSession's Section (see annotated_text_contracts.py). Each
 * block shows the beat's canonical `body_text` READ-ONLY (never copied —
 * always the live presentation text from the session's `blocks[]`
 * response) with the delivery-layer note editable below it via its own
 * lease, mirroring RoomBlockField's exact lock/autosave/conflict/draft-
 * recovery UI for a WorkDocument block.
 */
export default function AnnotatedTextRenderer({ workspaceId, instanceId, isViewAsClient, isInstanceLocked }) {
  const { data: view, isPending, isError } = useAnnotatedTextSession(workspaceId, instanceId);

  if (isPending) {
    return <p className="text-ink-soft text-sm" data-testid="annotated-text-loading">Loading…</p>;
  }
  if (isError || !view) {
    return <p className="text-terracotta text-sm" data-testid="annotated-text-error">Couldn't load this Annotated Text session.</p>;
  }

  const layerLabel = view.session.layer_key.replace(/_/g, " ");

  return (
    <div className="space-y-4" data-testid="annotated-text-renderer">
      <p className="text-xs text-ink-soft uppercase tracking-wide" data-testid="annotated-text-layer-label">Annotating: {layerLabel} delivery</p>
      {view.blocks.length === 0 ? (
        <p className="text-sm text-ink-soft italic" data-testid="annotated-text-empty">This section has no beats yet.</p>
      ) : (
        view.blocks.map((block) => (
          <AnnotatedTextBlock
            key={block.beat_id}
            workspaceId={workspaceId}
            atxId={view.session.atx_id}
            block={block}
            isViewAsClient={isViewAsClient || !view.can_edit}
            isInstanceLocked={isInstanceLocked}
          />
        ))
      )}
    </div>
  );
}

function AnnotatedTextBlock({ workspaceId, atxId, block, isViewAsClient, isInstanceLocked }) {
  const lease = useAnnotationLease({ workspaceId, atxId, block, isViewAsClient });
  const { register, unregister, setBlockDirty } = useRoomBlockRegistry();
  const beatId = block.beat_id;

  useEffect(() => {
    if (lease.isMine) register(beatId, lease.stopEditing);
    else unregister(beatId);
    return () => unregister(beatId);
  }, [lease.isMine, lease.stopEditing, beatId, register, unregister]);

  useEffect(() => {
    setBlockDirty(beatId, lease.dirty);
    return () => setBlockDirty(beatId, false);
  }, [lease.dirty, beatId, setBlockDirty]);

  const readOnly = isInstanceLocked || isViewAsClient || lease.isLockedByOther;

  return (
    <div
      className={`border rounded-lg p-4 ${lease.isMine ? "border-terracotta bg-terracotta/5" : "border-ink/15 bg-white/50"}`}
      data-testid={`annotated-text-block-${beatId}`}
    >
      <p className="text-sm text-ink flex items-start gap-2 mb-3" data-testid={`annotated-text-block-${beatId}-beat-text`}>
        <Quote size={13} className="text-ink-soft shrink-0 mt-1" />
        <span className="italic">{block.beat_body_text || <span className="text-ink-soft">(empty beat)</span>}</span>
      </p>

      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-xs uppercase tracking-wide text-ink-soft">Delivery note</p>
        {lease.isMine && lease.saveState !== "idle" && (
          <AutosaveStatus status={lease.saveState === "locked" ? "error" : lease.saveState} testId={`annotated-text-block-${beatId}-autosave-status`} />
        )}
      </div>

      {lease.isLockedByOther && (
        <p className="text-sm text-mustard-dark italic mb-2" data-testid={`annotated-text-block-${beatId}-locked-message`}>
          Another person is annotating this beat right now
        </p>
      )}

      {lease.recoverableDraft && !lease.isMine && (
        <div className="border border-mustard/50 bg-mustard/10 rounded-md p-3 mb-3 text-xs" data-testid={`annotated-text-block-${beatId}-recovery-banner`}>
          <p className="text-ink mb-2">You have unsaved text from an earlier session that was never confirmed as saved.</p>
          <div className="flex items-center gap-3">
            <button onMouseDown={(e) => e.preventDefault()} onClick={lease.keepLocalDraft} className="font-medium text-terracotta hover:text-terracotta/80" data-testid={`annotated-text-block-${beatId}-recovery-keep`}>
              Restore my draft
            </button>
            <button onMouseDown={(e) => e.preventDefault()} onClick={lease.discardLocalDraft} className="text-ink-soft hover:text-ink" data-testid={`annotated-text-block-${beatId}-recovery-discard`}>
              Discard, use latest
            </button>
          </div>
        </div>
      )}

      {lease.saveState === "conflict" && (
        <div className="border border-mustard/50 bg-mustard/10 rounded-md p-3 mb-3 text-xs" data-testid={`annotated-text-block-${beatId}-conflict-banner`}>
          <p className="text-ink mb-2">Someone else saved a newer version of this note. Your text was not lost — choose what to do:</p>
          <button onMouseDown={(e) => e.preventDefault()} onClick={lease.acceptLatestOnConflict} className="font-medium text-terracotta hover:text-terracotta/80" data-testid={`annotated-text-block-${beatId}-conflict-view-latest`}>
            View latest (discard my edit)
          </button>
        </div>
      )}

      {lease.isMine ? (
        <Textarea
          value={lease.localText}
          onChange={(e) => lease.onChangeText(e.target.value)}
          onBlur={() => lease.stopEditing()}
          autoFocus
          rows={3}
          placeholder="e.g. slow down here, breathe before the next line…"
          className="bg-white"
          data-testid={`annotated-text-block-${beatId}-editor`}
        />
      ) : (
        <div
          onClick={() => !readOnly && lease.startEditing()}
          className={`text-sm text-ink whitespace-pre-wrap min-h-[2.5rem] rounded-md px-2 py-1.5 -mx-2 ${!readOnly ? "cursor-text hover:bg-ink/5" : ""}`}
          data-testid={`annotated-text-block-${beatId}-preview`}
        >
          {lease.localText || <span className="text-ink-soft italic">Empty — {readOnly ? "read-only" : "click to write"}</span>}
        </div>
      )}

      <div className="flex items-center gap-2 mt-2">
        {readOnly && !lease.isLockedByOther && (
          <span className="inline-flex items-center gap-1 text-xs text-ink-soft" data-testid={`annotated-text-block-${beatId}-readonly-badge`}>
            <Lock size={11} /> Read-only
          </span>
        )}
        {!readOnly && !lease.isMine && !lease.isLockedByOther && (
          <button onClick={lease.startEditing} className="inline-flex items-center gap-1 text-xs text-terracotta hover:text-terracotta/80" data-testid={`annotated-text-block-${beatId}-start-edit`}>
            <Pencil size={11} /> Click to edit
          </button>
        )}
        {lease.isMine && (
          <button onClick={lease.stopEditing} className="inline-flex items-center gap-1 text-xs text-ink-soft hover:text-ink" data-testid={`annotated-text-block-${beatId}-done-edit`}>
            <Check size={11} /> Done editing
          </button>
        )}
      </div>
    </div>
  );
}
